/**
 * Reading the kind-specific payload off a board object.
 *
 * `BoardObject.data` is `Record<string, unknown>` by the data model (§3.2), because
 * what it holds depends on what the object is. Everything that reads it therefore has
 * to cope with a field that is missing or of the wrong type — an older lecture, a
 * newer version of the app, a board someone else's build wrote.
 *
 * The answer is always the same: take the value if it is the type expected, and fall
 * back to something sensible if it is not. Never throw, and never let `undefined`
 * reach the canvas.
 */

export function readString(
  data: Readonly<Record<string, unknown>>,
  key: string,
  fallback: string,
): string {
  const value = data[key];
  return typeof value === 'string' ? value : fallback;
}

export function readNumber(
  data: Readonly<Record<string, unknown>>,
  key: string,
  fallback: number,
): number {
  const value = data[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function readBoolean(
  data: Readonly<Record<string, unknown>>,
  key: string,
  fallback: boolean,
): boolean {
  const value = data[key];
  return typeof value === 'boolean' ? value : fallback;
}

/** One of a fixed set of words, or the fallback when it is anything else. */
export function readChoice<T extends string>(
  data: Readonly<Record<string, unknown>>,
  key: string,
  allowed: readonly T[],
  fallback: T,
): T {
  const value = data[key];
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}
